import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Paper,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TableContainer,
	Link,
	TextField,
	IconButton,
	Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import MyHeadings from '../Vendor-ui/Headings';
import { VendorApproved, VendorRequestPUTsingle } from '../../../../services/api/vendor';
import MySnackbarModal from '../Vendor-ui/SnackbarModal';
import VendorBalanceSummary from './VendorBalanceSummary';

interface Vendor {
	id: string;
	company: string;
	contactName: string;
	contactEmail: string;
	businessType: string;
	status: boolean;
	joined: string;
	_id: string;
	balanceToSettle?: number;
}

const MotionTableRow = motion(TableRow);

const VendorTable: React.FC = () => {
	const [vendors, setVendors] = useState<Vendor[]>([]);
	const [editRow, setEditRow] = useState<string | null>(null);
	const [editedBalance, setEditedBalance] = useState<{ [key: string]: number }>({});
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');
	const [snackbarSeverity, setSnackbarSeverity] = useState<'approve' | 'reject'>('approve');
	const navigate = useNavigate();

	const mapApiToVendors = (apiData: any[]): Vendor[] =>
		apiData.map((v) => ({
			id: v.vendorId,
			company: v.companyName,
			contactName: v.ownerName,
			contactEmail: v.email,
			businessType: v.companyType,
			status: v.status === 1,
			joined: new Date(v.createdAt).toLocaleDateString('en-IN'),
			_id: v._id,
			balanceToSettle: v.balanceToSettle || 0,
		}));

	const fetchApprovedData = async () => {
		try {
			const response = await VendorApproved();
			if (response?.success) {
				const mapped = mapApiToVendors(response.data.vendors);
				setVendors(mapped);
			}
		} catch (error) {
			console.error('Error fetching vendors:', error);
		}
	};

	const handleEditClick = (id: string, currentBalance: number) => {
		setEditRow(id);
		setEditedBalance((prev) => ({ ...prev, [id]: currentBalance }));
	};

	const handleSaveClick = async (id: string) => {
		try {
			const response = await VendorRequestPUTsingle(id, { settleAmount: editedBalance[id] });
			console.log(response, 'response');
			if (response?.success) {
				setVendors((prev) =>
					prev.map((v) =>
						v._id === id ? { ...v, balanceToSettle: editedBalance[id] } : v
					)
				);
				setSnackbarMessage(response.data.message);
				setSnackbarSeverity('approve');
			} else {
				console.log(response, 'response');
				setSnackbarMessage(response.data.errors);
				setSnackbarSeverity('reject');
			}
		} catch (error) {
			console.error('Error updating balance:', error);
			setSnackbarMessage('Error updating balance ❌');
			setSnackbarSeverity('reject');
		}
		setSnackbarOpen(true);
		setEditRow(null);
	};

	useEffect(() => {
		fetchApprovedData();
	}, []);

	return (
		<Box sx={{ width: '100%', p: { xs: 1, sm: 1, md: 2 } }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Box>
					<MyHeadings
						Title="Approved Vendors"
						subtitle="Manage your approved vendor partners"
					/>
				</Box>

				<Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
					<VendorBalanceSummary Title={'Total Balance to Settle'} vendors={vendors} />
					<VendorBalanceSummary Title={'Total Vendor'} vendors={vendors} />
				</Box>
			</Box>

			<Box sx={{ py: 2 }}>
				<TableContainer component={Paper} variant="outlined">
					<Table sx={{ minWidth: 1000 }}>
						<TableHead>
							<TableRow>
								<TableCell>Vendor ID</TableCell>
								<TableCell>Company</TableCell>
								<TableCell>Contact Person</TableCell>
								<TableCell>Business Type</TableCell>
								<TableCell>Joined</TableCell>
								<TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
									Balance to Settle
								</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{vendors.map((vendor, index) => (
								<MotionTableRow
									key={vendor.id}
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: index * 0.05 }}
								>
									<TableCell>
										<Box
											component={Link}
											href={`/vendor/approved/${vendor._id}`}
										>
											{vendor.id}
										</Box>
									</TableCell>
									<TableCell>{vendor.company}</TableCell>
									<TableCell>
										{vendor.contactName}
										<br />
										{vendor.contactEmail}
									</TableCell>
									<TableCell>{vendor.businessType}</TableCell>
									<TableCell>{vendor.joined}</TableCell>

									{/* Balance Column */}
									<TableCell>
										{editRow === vendor._id ? (
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													gap: 1,
												}}
											>
												<TextField
													size="small"
													type="number"
													value={editedBalance[vendor._id] ?? 0}
													onChange={(e) =>
														setEditedBalance((prev) => ({
															...prev,
															[vendor._id]: Number(e.target.value),
														}))
													}
													sx={{ width: 100 }}
												/>
												<IconButton
													color="primary"
													onClick={() => handleSaveClick(vendor._id)}
												>
													<SaveIcon />
												</IconButton>
												<IconButton
													color="error"
													onClick={() => setEditRow(null)}
												>
													<CloseIcon />
												</IconButton>
											</Box>
										) : (
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													gap: 1,
												}}
											>
												<span>{vendor.balanceToSettle}</span>

												<Tooltip title="Last Settlement Amount">
													<IconButton
														color="secondary"
														onClick={() =>
															handleEditClick(
																vendor._id,
																vendor.balanceToSettle || 0
															)
														}
													>
														<EditNoteIcon
															fontSize="small"
															sx={{ color: 'black' }}
														/>
													</IconButton>
												</Tooltip>
											</Box>
										)}
									</TableCell>

									<TableCell>
										<Box sx={{ display: 'flex', gap: 1 }}>
											<Button
												onClick={() => navigate(`${vendor._id}`)}
												size="small"
												startIcon={<VisibilityIcon />}
												sx={{
													backgroundColor: '#dbeafe',
													color: '#1e3a8a',
													border: '1px solid #93c5fd',
													textTransform: 'none',
												}}
											>
												View
											</Button>
											<Button
												onClick={() =>
													navigate(`/vendor/edit/${vendor._id}`)
												}
												size="small"
												startIcon={<EditIcon />}
												sx={{
													backgroundColor: '#fef3c7',
													color: '#92400e',
													border: '1px solid #fcd34d',
													textTransform: 'none',
												}}
											>
												Edit
											</Button>
										</Box>
									</TableCell>
								</MotionTableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>

			{/* ✅ Snackbar */}

			<MySnackbarModal
				open={snackbarOpen}
				action={snackbarSeverity}
				onClose={() => setSnackbarOpen(false)}
				Message={snackbarMessage}
			/>
		</Box>
	);
};

export default VendorTable;
